import React, { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { InlineMath, BlockMath } from "react-katex"
import "katex/dist/katex.min.css"
import { Loader2, Upload, CheckSquare, FileText, List, Pencil } from "lucide-react"
import { getAssignmentData } from "./api"
import jsPDF from "jspdf"

export default function ViewCreatedAssignment() {
  const { assignmentId } = useParams()
  const navigate = useNavigate()
  const [assignment, setAssignment] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchAssignment = async () => {
      setIsLoading(true)
      try {
        const response = await getAssignmentData(assignmentId)
        setAssignment(response.body.data)
      } catch (err) {
        console.error("Error fetching assignment:", err)
        setError("Failed to load assignment. Please try again.")
      } finally {
        setIsLoading(false)
      }
    }

    if (assignmentId) {
      fetchAssignment()
    }
  }, [assignmentId])

  const getQuestionTypeIcon = (question) => {
    if (question.mcqOptions && question.mcqOptions.length > 0) {
      return <CheckSquare size={20} className="text-blue-500" />
    } else if (question.questionType.toLowerCase().includes("short")) {
      return <FileText size={20} className="text-green-500" />
    } else if (question.questionType.toLowerCase().includes("essay")) {
      return <FileText size={20} className="text-purple-500" />
    } else {
      return <List size={20} className="text-gray-500" />
    }
  }

  const renderMathText = (text) => {
    if (!text) return null
    const parts = text.split(/(\$\$[\s\S]*?\$\$|\$[\s\S]*?\$)/)
    return parts.map((part, i) => {
      if (part.startsWith("$$") && part.endsWith("$$")) {
        return <BlockMath key={i}>{part.slice(2, -2)}</BlockMath>
      } else if (part.startsWith("$") && part.endsWith("$")) {
        return <InlineMath key={i}>{part.slice(1, -1)}</InlineMath>
      } else {
        return <span key={i}>{part}</span>
      }
    })
  }

  const downloadAsPDF = () => {
    const doc = new jsPDF()
    let yOffset = 10

    // Add title
    doc.setFontSize(18)
    doc.text(assignment.assignment_topic, 10, yOffset)
    yOffset += 10

    // Add teacher name
    doc.setFontSize(12)
    doc.text(
      `Created by: ${JSON.parse(localStorage.getItem("teacherData"))?.teacherName?.split(" ")[0] || "Teacher"}`,
      10,
      yOffset,
    )
    yOffset += 10

    // Add questions
    assignment.assignmentQuestion.forEach((question, index) => {
      yOffset += 10
      if (yOffset > 280) {
        doc.addPage()
        yOffset = 10
      }
      doc.setFontSize(14)
      doc.text(`${index + 1}. ${question.question}`, 10, yOffset)
      yOffset += 7
      doc.setFontSize(10)
      doc.text(
        `Type: ${question.mcqOptions && question.mcqOptions.length > 0 ? "Multiple Choice" : question.questionType} | Difficulty: ${question.questionDifficulty} | Max Marks: ${question.maxMarks}`,
        10,
        yOffset,
      )
      yOffset += 7

      if (question.mcqOptions && question.mcqOptions.length > 0) {
        question.mcqOptions.forEach((option, optionIndex) => {
          if (yOffset > 280) {
            doc.addPage()
            yOffset = 10
          }
          doc.text(`${optionIndex + 1}. ${option}`, 15, yOffset)
          yOffset += 7
        })
      }
    })

    doc.save(`${assignment.assignment_topic}.pdf`)
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center">
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <Loader2 className="animate-spin h-16 w-16 text-blue-500 mx-auto" />
          <p className="text-center mt-4">Loading assignment data...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="bg-white p-6 rounded-lg shadow-lg text-center">
          <Loader2 className="mx-auto text-red-500 mb-4" size={48} />
          <p className="text-xl font-semibold text-gray-800">{error}</p>
        </div>
      </div>
    )
  }

  if (!assignment) {
    return null
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-8">
        <h1 className="text-3xl font-bold text-center text-gray-800 mb-8">
          {assignment.assignment_topic || "View Assignment"}
        </h1>

        <div className="flex justify-between mb-6">
          <button
            onClick={downloadAsPDF}
            className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
          >
            <Upload size={20} />
            <span>Download as PDF</span>
          </button>
        </div>

        <div className="space-y-4">
          {assignment.assignmentQuestion &&
            assignment.assignmentQuestion.map((question, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-sm mb-4 border border-gray-200">
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1">
                    <h2 className="text-lg font-semibold text-gray-700 mb-2">Question {index + 1}</h2>
                    <p className="text-sm text-gray-500 mb-2">
                      Type:{" "}
                      {question.mcqOptions && question.mcqOptions.length > 0
                        ? "Multiple Choice"
                        : question.questionType}{" "}
                      | Difficulty: {question.questionDifficulty || "N/A"}
                    </p>
                    <div className="text-gray-600 whitespace-pre-wrap mb-4">{renderMathText(question.question)}</div>
                    {question.mcqOptions && question.mcqOptions.length > 0 && (
                      <ol className="list-decimal list-inside space-y-2 pl-4">
                        {question.mcqOptions.map((option, i) => (
                          <li key={i} className="text-gray-700">
                            {renderMathText(option)}
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 items-end">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Marks:</span>
                      <span className="font-semibold">{question.maxMarks || 0}</span>
                    </div>
                    <div className="flex items-center">
                      {getQuestionTypeIcon(question)}
                      <span className="ml-2 text-sm font-medium text-gray-500">
                        {question.mcqOptions && question.mcqOptions.length > 0
                          ? "Multiple Choice"
                          : question.questionType}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>

        <div className="mt-8 flex justify-between items-center">
          <p className="text-xl font-bold text-gray-800">
            Total Marks:{" "}
            {assignment.assignmentQuestion
              ? assignment.assignmentQuestion.reduce((total, q) => total + (q.maxMarks || 0), 0)
              : 0}
          </p>
        </div>
      </div>
    </div>
  )
}

